import { useParams, Navigate, Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, MapPin, Star, Phone, MessageCircle, ArrowRight, GraduationCap, Sparkles } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import RegistrationForm from "../components/RegistrationForm";
import WhyChooseAnandTutorials from "../components/WhyChooseAnandTutorials";
import { getCityTopicData, SEO_TOPICS } from "../data/cityTopicData";
import { useSettings } from "../context/SettingsContext";

/**
 * Programmatic SEO city × topic landing page with optional ?area= locality variation.
 * URL pattern: /<topicSlug>-<urlVerb>-<citySlug>[?area=<locality>]
 *   e.g. /cbse-home-tuition-jabalpur?area=Vijay+Nagar
 *
 * 36 base pages × ~25 localities each = ~900 unique hyperlocal landing variations.
 */
export default function CityTopicLanding({ topicSlug, citySlug }) {
    const { settings } = useSettings();
    const [searchParams] = useSearchParams();
    const data = getCityTopicData(topicSlug, citySlug);
    if (!data) return <Navigate to="/" replace />;

    const phone = settings.head_office_phone || data.city.phone;
    const whatsapp = settings.whatsapp_number || "917000530316";

    // Validate ?area= against the city's known localities (anti-spam, prevents arbitrary injection)
    const requestedArea = searchParams.get("area") || "";
    const matchedArea = data.topAreas.find(
        (a) => a.toLowerCase() === requestedArea.toLowerCase().trim()
    );
    const area = matchedArea || null;

    // Dynamic title/description/H1 when locality is specified
    const seoTitle = area
        ? `${data.topic.label} ${data.topic.h1Verb} in ${area}, ${data.cityName} — Verified Tutors | Anand Tutorials`
        : data.seoTitle;
    const seoDescription = area
        ? `Best ${data.topic.label} ${data.topic.h1Verb.toLowerCase()} in ${area}, ${data.cityName}. Verified 1:1 home & online tutors, free demo class. ${data.topic.formal} specialists since 2013. Call ${phone}.`
        : data.seoDescription;
    const h1 = area ? `${data.topic.label} ${data.topic.h1Verb} in ${area}, ${data.cityName}` : data.h1;
    const intro = area
        ? `Looking for the best ${data.topic.label} ${data.topic.h1Verb.toLowerCase()} in ${area}, ${data.cityName}? Anand Tutorials matches your child with a verified ${data.topic.formal} specialist tutor — at home in ${area} or live online — with deep familiarity of local schools and ${data.cityName}'s academic patterns. ${data.cityName}'s most trusted name in 1-on-1 academic tutoring since 2013.`
        : data.intro;
    const eyebrow = area ? `${area} · ${data.cityName}` : data.eyebrow;

    // Canonical URL — include area param so Google indexes locality variants as distinct pages
    const canonicalPath = area ? `${data.slug}?area=${encodeURIComponent(area)}` : data.slug;

    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": `${data.topic.label} ${data.topic.h1Verb}`,
        "provider": {
            "@type": "EducationalOrganization",
            "name": "Anand Tutorials",
            "url": "https://anandtutorials.com",
            "telephone": phone,
            "address": {
                "@type": "PostalAddress",
                "addressLocality": area || data.cityName,
                "addressRegion": data.city.state,
                "postalCode": data.city.postalCode,
                "addressCountry": "IN",
            },
        },
        "areaServed": area
            ? {
                "@type": "Place",
                "name": `${area}, ${data.cityName}`,
                "containedInPlace": { "@type": "City", "name": data.cityName },
            }
            : {
                "@type": "City",
                "name": data.cityName,
                "containedInPlace": { "@type": "AdministrativeArea", "name": data.city.state },
            },
        "name": h1,
        "description": intro,
        "url": `https://anandtutorials.com${canonicalPath}`,
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "850",
            "bestRating": "5",
            "worstRating": "1",
        },
    };

    // FAQs — area-aware first FAQ
    const faqsToShow = area
        ? [
            [
                `Do you provide ${data.topic.label} ${data.topic.h1Verb.toLowerCase()} in ${area}?`,
                `Yes — ${area} is one of our active service zones in ${data.cityName}. We provide verified 1-on-1 ${data.topic.label} tutors who reach your home in ${area} within 24-48 hours of booking.`,
            ],
            ...data.faqs.slice(1),
        ]
        : data.faqs;

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqsToShow.map(([q, a]) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    };

    return (
        <div data-testid={`city-topic-${topicSlug}-${citySlug}${area ? `-${area.toLowerCase().replace(/\s+/g, "-")}` : ""}`} className="bg-white">
            <SEO
                title={seoTitle}
                description={seoDescription}
                keywords={data.keywords}
                path={canonicalPath}
                schema={serviceSchema}
            />
            <SEO schema={faqSchema} />

            <Breadcrumbs items={[
                { name: data.cityName, to: `/${citySlug}` },
                { name: data.h1, to: data.slug },
                ...(area ? [{ name: area }] : []),
            ]} />

            <PageHeader
                eyebrow={eyebrow}
                title={h1}
                subtitle={intro}
            >
                <div className="flex flex-wrap gap-3">
                    <Link to="/register" data-testid="city-topic-cta-demo" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        Book Free Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href={`tel:${phone.replace(/\s/g, "")}`} data-testid="city-topic-cta-call" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/20 transition-colors">
                        <Phone className="w-4 h-4" /> Call Us
                    </a>
                </div>
            </PageHeader>

            {/* Why local */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3 flex items-center gap-2">
                        <Sparkles className="w-3 h-3" /> Why {area ? `${area}, ${data.cityName}` : `${data.cityName}`} families choose us
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-8">
                        {area || data.cityName}'s most trusted name in {data.topic.label} 1:1 tutoring.
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {data.whyLocal.map((w, i) => (
                            <div key={i} className="flex items-start gap-3 p-5 rounded-2xl bg-[#F8FAFC] border border-slate-200">
                                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                                <div className="text-sm text-[#0F172A] leading-relaxed">{w}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE — EEAT block */}
            <WhyChooseAnandTutorials
                eyebrow="Why parents trust us"
                headline={`Why we are ${data.cityName}'s most trusted ${data.topic.label} 1:1 brand.`}
                contextLabel={`for ${data.topic.label} ${data.topic.h1Verb.toLowerCase()} in ${data.cityName} and pan-India online`}
            />

            {/* Localities served — internal-link surface that creates ~25 hyperlocal variants per page */}
            {data.topAreas.length > 0 && (
                <section className="py-16 bg-[#F8FAFC] border-y border-slate-100">
                    <div className="max-w-5xl mx-auto px-6 lg:px-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-600 mb-3 flex items-center gap-2">
                            <MapPin className="w-3 h-3" /> {data.topic.label} {data.topic.h1Verb} across every {data.cityName} locality
                        </div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-2">
                            Tap your area for {data.cityName}-specific {data.topic.label} tutors.
                        </h2>
                        <p className="text-sm text-slate-500 mb-8">
                            A verified {data.topic.label} tutor reaches your home within 24-48 hours of booking.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {data.topAreas.map((a) => {
                                const isActive = matchedArea && matchedArea.toLowerCase() === a.toLowerCase();
                                return (
                                    <Link
                                        key={a}
                                        to={`${data.slug}?area=${encodeURIComponent(a)}`}
                                        data-testid={`area-link-${a.toLowerCase().replace(/\s+/g, "-")}`}
                                        aria-label={`${data.topic.label} ${data.topic.h1Verb} in ${a}, ${data.cityName}`}
                                        className={`inline-flex items-center px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${isActive ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-200 hover:border-[#1E3A8A] hover:text-[#1E3A8A]"}`}
                                    >
                                        {a}
                                    </Link>
                                );
                            })}
                        </div>
                        <p className="mt-6 text-xs text-slate-500">
                            Don't see your locality? <a href={`tel:${phone.replace(/\s/g, "")}`} className="text-[#1E3A8A] font-semibold underline">Call us</a> — chances are we serve it too.
                        </p>
                    </div>
                </section>
            )}

            {/* FAQs + Registration */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14">
                    <div className="lg:col-span-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3">FAQs</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-6">
                            {area || data.cityName} parents ask — here's what we tell them.
                        </h2>
                        <div className="space-y-3">
                            {faqsToShow.map(([q, a], i) => (
                                <details key={q} data-testid={`city-topic-faq-${i}`} className="group bg-[#F8FAFC] border border-slate-200 rounded-xl p-5 [&_summary::-webkit-details-marker]:hidden">
                                    <summary className="cursor-pointer font-heading font-semibold text-[#0F172A] flex items-center justify-between gap-3">
                                        {q}
                                        <span className="text-[#1E3A8A] text-lg group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <div className="text-sm text-slate-600 mt-3 leading-relaxed">{a}</div>
                                </details>
                            ))}
                        </div>

                        <div className="mt-10 p-5 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300 mb-1">Talk to us today</div>
                                <div className="font-heading font-semibold">Book a free {data.topic.label} demo class in {area || data.cityName}.</div>
                            </div>
                            <a
                                href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi — I'd like a free demo for ${data.topic.label} ${data.topic.h1Verb} in ${area ? `${area}, ` : ""}${data.cityName}`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="city-topic-whatsapp"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#22C55E] hover:bg-[#16A34A] text-white text-sm font-semibold"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                            </a>
                        </div>

                        {/* Related city pages */}
                        <div className="mt-10 pt-8 border-t border-slate-100">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 mb-3">Related pages</div>
                            <div className="flex flex-wrap gap-2">
                                <Link to={`/${citySlug}`} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-[#1E3A8A] hover:text-white text-slate-700 font-semibold transition-colors">
                                    All {data.cityName} home tuition
                                </Link>
                                {Object.keys(SEO_TOPICS).filter((t) => t !== topicSlug).slice(0, 5).map((t) => {
                                    const info = SEO_TOPICS[t];
                                    return (
                                        <Link
                                            key={t}
                                            to={`/${t}-${info.urlVerb}-${citySlug}`}
                                            className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-[#1E3A8A] hover:text-white text-slate-700 font-semibold transition-colors"
                                        >
                                            {info.label} in {data.cityName}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="sticky top-24">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-3">
                                <GraduationCap className="w-3 h-3" /> Free demo
                            </div>
                            <h3 className="font-heading text-xl font-bold text-[#0F172A] mb-1">
                                Book your demo {area ? `in ${area}` : `in ${data.cityName}`}
                            </h3>
                            <p className="text-xs text-slate-500 mb-4">
                                <Star className="w-3 h-3 inline-block mr-1 fill-amber-400 text-amber-400" />
                                4.8 / 5 across 850+ {data.cityName} families
                            </p>
                            <RegistrationForm />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
